import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { rastrear, nomeDoPlano } from "@/lib/planos";
import { toast } from "sonner";
import { API } from "@/lib/api";


export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { formatApiError, setUserDirect } = useAuth();
  const sessionId = searchParams.get("session_id");

  const [verifying, setVerifying] = useState(true);
  const [paymentValid, setPaymentValid] = useState(false);
  const [pollCount, setPollCount] = useState(0);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [aceite, setAceite] = useState(false);
  const [jaUsado, setJaUsado] = useState(null); // "renovacao" | "conta"
  const [plano, setPlano] = useState("");

  useEffect(() => {
    if (!sessionId) {
      setVerifying(false);
      return;
    }
    verifyPayment();
  }, [sessionId]);

  const verifyPayment = async (attempt = 0) => {
    const maxAttempts = 15;
    const delay = attempt < 3 ? 2000 : 3000;
    try {
      const res = await axios.get(`${API}/stripe/verify-session/${sessionId}`, { timeout: 10000 });
      if (res.data.payment_status === "paid") {
        setPaymentValid(true);
        setVerifying(false);
        setPlano(res.data.plano || "");
        if (res.data.renovacao) setJaUsado("renovacao");
        else if (res.data.conta_criada) setJaUsado("conta");
        if (res.data.customer_email) {
          setEmail(res.data.customer_email);
        }
        return;
      }
      if (attempt < maxAttempts) {
        setPollCount(attempt + 1);
        setTimeout(() => verifyPayment(attempt + 1), delay);
      } else {
        setVerifying(false);
      }
    } catch {
      if (attempt < maxAttempts) {
        setPollCount(attempt + 1);
        setTimeout(() => verifyPayment(attempt + 1), delay);
      } else {
        setVerifying(false);
      }
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }
    if (password.length < 8) {
      toast.error("Senha deve ter pelo menos 8 caracteres");
      return;
    }
    if (!aceite) {
      toast.error("Para criar a conta, aceite os Termos de Uso e a Política de Privacidade");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register-paid`, {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        session_id: sessionId,
        aceite_termos: aceite
      });
      if (res.data.token) {
        localStorage.setItem("auth_token", res.data.token);
      }
      setUserDirect(res.data);
      rastrear("CompleteRegistration", { content_name: "Aesthetica Pricing Pro", status: plano || "assinatura" });
      toast.success("Conta criada com sucesso! Bem-vinda!");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  // No session_id provided
  if (!sessionId) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-slate-200 dark:border-slate-800 shadow-lg">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Escolha um plano primeiro</h2>
            <p className="text-slate-500 dark:text-slate-400">
              Para criar sua conta, escolha um plano e conclua o pagamento.
            </p>
            <Button
              onClick={() => { window.location.href = "/#planos"; }}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="go-to-checkout-btn"
            >
              Ver planos
            </Button>
            <div>
              <button
                onClick={() => navigate("/login")}
                className="text-sm text-teal-600 hover:text-teal-700"
                data-testid="back-to-login-link"
              >
                Já tem conta? Entrar
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Verifying payment
  if (verifying) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
        <div className="text-center space-y-4 max-w-sm" data-testid="payment-verifying">
          <Loader2 className="w-12 h-12 text-teal-600 animate-spin mx-auto" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Verificando seu pagamento...</h2>
          <p className="text-slate-500 dark:text-slate-400">Aguarde, estamos confirmando com o Stripe.</p>
          {pollCount > 2 && (
            <p className="text-sm text-slate-400">Conectando ao servidor... Isso pode levar alguns segundos.</p>
          )}
        </div>
      </div>
    );
  }

  // Payment not confirmed
  if (!paymentValid) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-slate-200 dark:border-slate-800 shadow-lg">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Pagamento não confirmado</h2>
            <p className="text-slate-500 dark:text-slate-400">
              Não foi possível confirmar seu pagamento. Tente novamente ou fale com o suporte.
            </p>
            <div className="space-y-2">
              <Button
                onClick={() => { setVerifying(true); setPollCount(0); verifyPayment(); }}
                className="w-full bg-teal-600 hover:bg-teal-700"
                data-testid="retry-verify-btn"
              >
                Verificar novamente
              </Button>
              <Button
                variant="outline"
                onClick={() => { window.location.href = "/#planos"; }}
                className="w-full"
                data-testid="retry-checkout-btn"
              >
                Ver planos
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (jaUsado) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-slate-200 dark:border-slate-800 shadow-lg">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <CheckCircle className="w-12 h-12 text-green-600 mx-auto" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Pagamento confirmado</h2>
            <p className="text-slate-500 dark:text-slate-400">
              {jaUsado === "renovacao"
                ? "Este pagamento renovou uma conta que já existe. É só entrar."
                : "A conta deste pagamento já foi criada. É só entrar com o e-mail e a senha cadastrados."}
            </p>
            <Button onClick={() => navigate("/login")} className="w-full bg-teal-600 hover:bg-teal-700">Entrar</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Payment confirmed - show registration form
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-teal-700 dark:text-teal-400" style={{ fontFamily: 'Manrope' }}>
            Aesthetica
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Pricing Pro</p>
        </div>

        <Card className="border-slate-200 dark:border-slate-800 shadow-lg" data-testid="register-card">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2 justify-center text-green-600 mb-2">
              <CheckCircle className="w-5 h-5" />
              <span className="text-sm font-medium">Pagamento aprovado!</span>
            </div>
            <CardTitle className="text-xl text-center text-slate-900 dark:text-white">
              Criar sua conta
            </CardTitle>
            <p className="text-sm text-center text-slate-500 dark:text-slate-400 mt-1">
              {plano ? `Plano ${nomeDoPlano(plano)} ativo. ` : ""}Preencha seus dados para acessar o app.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome completo</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Seu nome"
                  required
                  data-testid="register-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                  data-testid="register-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimo 8 caracteres"
                  required
                  data-testid="register-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirmar senha</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repita a senha"
                  required
                  data-testid="register-confirm-input"
                />
              </div>
              <div className="flex items-start gap-2">
                <Checkbox id="aceite" checked={aceite} onCheckedChange={(v) => setAceite(Boolean(v))} data-testid="register-terms-checkbox" />
                <label htmlFor="aceite" className="text-sm leading-snug text-slate-600 dark:text-slate-300">
                  Li e aceito os{" "}
                  <a href="/termos" target="_blank" rel="noreferrer" className="text-teal-600 underline">Termos de Uso</a> e a{" "}
                  <a href="/privacidade" target="_blank" rel="noreferrer" className="text-teal-600 underline">Política de Privacidade</a>.
                </label>
              </div>
              <Button
                type="submit"
                className="w-full bg-teal-600 hover:bg-teal-700"
                disabled={loading || !aceite}
                data-testid="register-submit-btn"
              >
                {loading ? "Criando conta..." : "Criar conta e acessar"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
